import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@mui/material';
import { Vehicle, EditField } from '../types';
import { TASK_TYPE_CONFIG, getTaskBgColor } from '../constants/taskConfig';
import MMCVehicleCanvas from './MMCVehicleCanvas';

const THEME_COLOR = TASK_TYPE_CONFIG.mmc.color;

interface MMCCardProps {
  vehicle: Vehicle;
  imageBaseUrl: string;
  isSelected: boolean;
  onToggleSelection: (vehicleId: string, shiftKey?: boolean) => void;
  onBoxUpdate: (vehicleId: string, boxArray: number[] | null) => void;
  onEditField: (vehicle: Vehicle, field: EditField) => void;
  onHide?: (vehicleId: string) => void;
  hideSelection?: boolean;
  onCopyId?: (vehicleId: string) => void;
  onToggleColorChecked?: (vehicle: Vehicle) => void;
  onVinClick?: (vin: string) => void;
  onDelete?: (vehicleId: string) => void;
}

const MMCCard: React.FC<MMCCardProps> = ({
  vehicle,
  imageBaseUrl,
  isSelected,
  onToggleSelection,
  onBoxUpdate,
  onEditField,
  onHide,
  hideSelection = false,
  onCopyId,
  onToggleColorChecked,
  onVinClick,
  onDelete,
}) => {
  const theme = useTheme();
  const [overlaysHidden, setOverlaysHidden] = useState(false);
  const [imageExpanded, setImageExpanded] = useState(false);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setOverlaysHidden(prev => !prev);
  }, []);

  return (
  <>
    <Paper
      elevation={isSelected ? 4 : 1}
      sx={{
        overflow: 'hidden',
        bgcolor: isSelected ? getTaskBgColor('mmc', theme.palette.mode) : 'transparent',
        border: isSelected ? `2px solid ${THEME_COLOR}` : '2px solid transparent',
      }}
    >
      {/* Image with overlays */}
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '4/3',
          bgcolor: 'action.disabledBackground',
          width: '240px',
          height: '180px',
        }}
        onContextMenu={handleContextMenu}
      >
        <MMCVehicleCanvas
          URL={`${imageBaseUrl}/${vehicle.id}`}
          onBoxUpdate={(newBoxArray) => onBoxUpdate(vehicle.id, newBoxArray)}
          boxArray={vehicle.box}
          width={240}
          height={180}
        />
        {!overlaysHidden && (
          <>
            {/* Selection checkbox and hide button - top left */}
            {!hideSelection && (
              <Box sx={{ position: 'absolute', top: 4, left: 4, display: 'flex', gap: 0.5 }}>
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => onToggleSelection(vehicle.id, e.nativeEvent instanceof MouseEvent && e.nativeEvent.shiftKey)}
                  sx={{
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)',
                    borderRadius: 1,
                    p: 0.5,
                    '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.95)' },
                  }}
                  size="small"
                />
                {onHide && (
                  <IconButton
                    size="small"
                    onClick={() => onHide(vehicle.id)}
                    title="Hide item"
                    sx={{
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)',
                      borderRadius: 1,
                      p: 0.5,
                      '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.95)' },
                    }}
                  >
                    <VisibilityOffIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Box>
            )}
            {/* Top right controls - expand button and year */}
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                display: 'flex',
                gap: 0.5,
              }}
            >
              {/* Expand button */}
              <Tooltip title="Expand image">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setImageExpanded(true);
                  }}
                  sx={{
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)',
                    p: 0.5,
                    '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.95)' },
                  }}
                >
                  <ZoomOutMapIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {/* Year */}
              <Box
                onClick={() => onEditField(vehicle, 'year')}
                sx={{
                  bgcolor: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                  {vehicle.year || '—'}
                </Typography>
              </Box>
            </Box>
            {/* Make & Model - bottom overlay */}
            <Box
              onClick={() => onEditField(vehicle, 'make')}
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: '#fff',
                px: 1,
                py: 0.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
              }}
            >
              <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                {vehicle.model?.serie?.make?.label || 'Unknown Make'}{' '}
                <Typography
                  component="span"
                  variant="subtitle2"
                  sx={{ fontWeight: 400, fontSize: '0.8rem' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditField(vehicle, 'model');
                  }}
                >
                  {vehicle.model?.label || ''}
                </Typography>
              </Typography>
            </Box>
          </>
        )}
      </Box>
      {/* Vehicle Info */}
      <Box sx={{ p: 1 }}>
        {/* Body & Type - clickable */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Box
            onClick={() => onEditField(vehicle, 'body')}
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 0.5, px: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {vehicle.body?.label || '—'}
            </Typography>
          </Box>
          <Box sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 0.5, px: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              •
            </Typography>
          </Box>
          <Box
            onClick={() => onEditField(vehicle, 'type')}
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 0.5, px: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {vehicle.type?.label || '—'}
            </Typography>
          </Box>
        </Box>
        {/* Colors - clickable */}
        <Box
          onClick={() => onEditField(vehicle, 'colors')}
          sx={{
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
            borderRadius: 0.5,
            px: 0.5,
            mx: -0.5,
            mt: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {vehicle.colors && vehicle.colors.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {vehicle.colors.slice(0, 3).map((color) => (
                <Box key={color.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: color.hex || '#ccc',
                      border: 1, borderColor: 'divider',
                    }}
                  />
                  <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                    {color.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              No colors
            </Typography>
          )}
          {/* Color verified indicator - clickable to toggle */}
          <Tooltip title={vehicle.colorChecked ? 'Color verified (click to toggle)' : 'Color not verified (click to toggle)'}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                if (onToggleColorChecked) {
                  onToggleColorChecked(vehicle);
                }
              }}
              sx={{ p: 0, ml: 0.5 }}
            >
              {vehicle.colorChecked ? (
                <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        {/* Dataset - clickable */}
        <Box
          onClick={() => onEditField(vehicle, 'dataset')}
          sx={{
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
            borderRadius: 0.5,
            px: 0.5,
            mx: -0.5,
            mt: 0.5,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
            {vehicle.dataset?.label || 'No dataset'}
          </Typography>
        </Box>
        {/* VIN - clickable */}
        {vehicle.vin && (
          <Box sx={{ mt: 0.5, px: 0.5, mx: -0.5 }}>
            {onVinClick ? (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  fontFamily: 'monospace',
                  color: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => onVinClick(vehicle.vin!)}
              >
                {vehicle.vin}
              </Typography>
            ) : (
              <Typography
                component="a"
                href={`/?p=searchMmc&vin=${encodeURIComponent(vehicle.vin)}&pageSize=200`}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  fontFamily: 'monospace',
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {vehicle.vin}
              </Typography>
            )}
          </Box>
        )}
        {/* Flags - clickable */}
        <Box
          onClick={() => onEditField(vehicle, 'flags')}
          sx={{
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
            borderRadius: 0.5,
            px: 0.5,
            mx: -0.5,
            mt: 0.5,
          }}
        >
          {vehicle.flags && vehicle.flags.length > 0 ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {vehicle.flags.map((flag) => (
                <Chip
                  key={flag.id}
                  label={flag.label}
                  size="small"
                  sx={{ height: 16, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              No flags
            </Typography>
          )}
        </Box>
        {/* ID with copy button */}
        {onCopyId && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.5,
              px: 0.5,
              mx: -0.5,
            }}
          >
            <Tooltip title={vehicle.id}>
              <Typography
                variant="caption"
                sx={{ fontSize: '0.55rem', fontFamily: 'monospace', color: 'text.secondary' }}
              >
                {vehicle.id.slice(0, 8)}...{vehicle.id.slice(-4)}
              </Typography>
            </Tooltip>
            <Tooltip title="Copy ID">
              <IconButton
                size="small"
                onClick={() => onCopyId(vehicle.id)}
                sx={{ p: 0 }}
              >
                <ContentCopyIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
            {onDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => onDelete(vehicle.id)}
                  sx={{ p: 0, ml: 'auto' }}
                >
                  <DeleteIcon sx={{ fontSize: 14, color: 'error.main' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Paper>

    {/* Fullscreen Image Dialog */}
    <Dialog
      open={imageExpanded}
      onClose={() => setImageExpanded(false)}
      maxWidth="lg"
    >
      <Box
        component="img"
        src={`${imageBaseUrl}/${vehicle.id}`}
        alt={`${vehicle.model?.serie?.make?.label || 'Unknown'} ${vehicle.model?.label || ''}`}
        sx={{
          maxWidth: '90vw',
          maxHeight: '90vh',
          objectFit: 'contain',
        }}
      />
    </Dialog>
  </>
  );
};

export default React.memo(MMCCard);
